import {
  createContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useCallback,
} from 'react';
import * as Sentry from '@sentry/react';
import { Org, User, UserResponse } from '@/types';
import { analytics } from '@/lib/analytics';
import { fetchUser } from '@/services/auth/fetch-user.ts';
import { validateInvite } from '@/services/members/validate-invite.ts';
import { validateOtp } from '@/services/auth/validate-otp.ts';
import { useOrgStore } from '@/stores/org-store';
import { getToken } from '@/services/auth/get-token.ts';
import { getAllOrgs } from '@/services/org/get-all-org';

import { useCookieConsent } from '@/context/cookie-consent-context';
import { signOut } from '@/services/auth/sign-out';
import { ROUTES } from '@/constants/routes';
import { parseInviteParams } from '@/utils/parse-url-params';
import { readOrgParam, stripOrgParamFromUrl } from '@/utils/org-deep-link';
import { useAuth0Safe } from '@/context/auth0-context';
import {
  setAuth0TokenProvider,
  clearAuth0TokenProvider,
} from '@/utils/auth-token-provider';
import { clearAuth0Cache, revokeAuth0RefreshTokens } from '@/utils/auth0-cache';
import { LAST_ACTIVITY_KEY } from '@/constants/session-keys';
import { clearLoggingOut, markLoggingOut } from '@/utils/logout-state';
import { broadcastLogout } from '@/utils/logout-broadcast';

/** Errors indicating stale/invalid Auth0 cached state that should be cleared. */
const STALE_AUTH0_ERRORS = [
  'missing refresh token',
  'invalid refresh token',
  'login required',
  'unknown or invalid refresh token',
];

function isStaleAuth0Error(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message.toLowerCase()
      : String(error).toLowerCase();
  return STALE_AUTH0_ERRORS.some((pattern) => message.includes(pattern));
}

export type OrgDeepLinkMessage = {
  text: string;
  severity: 'success' | 'warning';
};

interface AuthContextProps {
  user: User | null;
  loading: boolean;
  isValidating: boolean;
  isAdmin: boolean;
  resetUser: () => void;
  updateUser: (data: UserResponse) => void;
  refetchAuth: () => Promise<
    | {
        userResponse?: { detail?: string };
        tokenResponse?: { detail?: string };
      }
    | undefined
  >;
  orgSwitchBlockedMessage: string | null;
  clearOrgSwitchBlockedMessage: () => void;
  orgDeepLinkMessage: OrgDeepLinkMessage | null;
  clearOrgDeepLinkMessage: () => void;
}

/**
 * Creates an empty organization object used as a fallback when org fetch fails
 * or no valid organization is found.
 */
const createEmptyOrg = (): Org => ({
  orgid: '',
  name: '',
  user_role: undefined,
  branding: {
    logo_url: '',
    primary_color: '',
  },
});

export const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: true,
  isValidating: false,
  isAdmin: false,
  resetUser: () => {},
  updateUser: () => {},
  refetchAuth: async () => undefined,
  orgSwitchBlockedMessage: null,
  clearOrgSwitchBlockedMessage: () => {},
  orgDeepLinkMessage: null,
  clearOrgDeepLinkMessage: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [orgSwitchBlockedMessage, setOrgSwitchBlockedMessage] = useState<
    string | null
  >(null);
  const [orgDeepLinkMessage, setOrgDeepLinkMessage] =
    useState<OrgDeepLinkMessage | null>(null);
  const { cc } = useCookieConsent();
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
  const isLocalhost =
    environment === 'localhost' || window.location.hostname === 'localhost';

  const { email, code } = parseInviteParams();
  const { setToken, setAuthType, setOrgs, setCurrentOrg } = useOrgStore();
  const authInProgressRef = useRef(false);
  const {
    isAuthenticated: isAuth0Authenticated,
    isLoading: isAuth0Loading,
    user: auth0User,
    getAccessTokenSilently,
    logout: auth0Logout,
  } = useAuth0Safe();
  const isAdmin = user?.isAdmin ?? false;

  const updateUser = useCallback(
    (data: UserResponse, currentOrgData?: Org) => {
      const userData: User = {
        id: data?.id,
        firstName: data.firstName,
        lastName: data?.lastName,
        isAdmin: data?.typeName === 'Admin',
        email: data?.email,
        image: data?.image,
        isOwner: currentOrgData?.user_role === 'owner',
        // Per-org capability, enriched onto the org by platform-api. Never
        // sourced from the JWT claim — see the note at the call site.
        is_infra_admin: currentOrgData?.is_infra_admin ?? false,
      };

      setUser(userData);
    },
    [setUser],
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  const handleAuth0Session = useCallback(async () => {
    try {
      console.log('Auth0: handleAuth0Session started', {
        hasAuth0User: !!auth0User,
        auth0Email: auth0User?.email,
        timestamp: new Date().toISOString(),
      });

      const tokenStartTime = performance.now();
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        },
      });
      const tokenDuration = performance.now() - tokenStartTime;

      console.log('Auth0: getAccessTokenSilently completed', {
        hasToken: !!accessToken,
        tokenLength: accessToken?.length,
        durationMs: Math.round(tokenDuration),
        timestamp: new Date().toISOString(),
      });

      setToken(accessToken);
      setAuthType('auth0');

      // Register the token provider so non-React code (apiRequest, WebSocket services)
      // can obtain fresh tokens. The wrapper bakes in the audience param; callers can
      // pass cacheMode to control cache behavior (e.g., 'off' on 401 retry).
      setAuth0TokenProvider((options) =>
        getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          },
          ...options,
        }),
      );

      // Decode access token to check platform admin status and org_id from Auth0 claims
      let isTokenAdmin = false;
      let jwtOrgId: string | null = null;
      let userEmail: string | null = null;
      try {
        const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
        const roles: string[] = tokenPayload['https://capitol.ai/roles'] || [];
        isTokenAdmin = roles.includes('admin');
        jwtOrgId = tokenPayload['https://capitol.ai/org_id'] || null;
        userEmail = tokenPayload['https://capitol.ai/email'] || null;
      } catch {
        // Token decode failed — default to non-admin, no org preference
      }

      // Construct User from Auth0 user profile claims
      const userData: User = {
        id: auth0User?.sub || '',
        firstName:
          auth0User?.given_name || auth0User?.name?.split(' ')[0] || '',
        lastName:
          auth0User?.family_name ||
          auth0User?.name?.split(' ').slice(1).join(' ') ||
          '',
        isAdmin: isTokenAdmin,
        email: auth0User?.email || userEmail || '',
        image: auth0User?.picture || '',
        isOwner: false,
        is_infra_admin: false,
      };

      setUser(userData);

      // Fetch organizations using the Auth0 token
      const orgsResponse = await getAllOrgs();

      if (orgsResponse?.detail) {
        console.error(
          'Failed to fetch orgs for Auth0 user:',
          orgsResponse.detail,
        );
        setOrgs([]);
      } else {
        setOrgs(orgsResponse.data as Org[]);
      }

      // Set current org from response
      if (!orgsResponse?.detail && orgsResponse?.data) {
        const orgs = orgsResponse.data as Org[];

        if (orgs.length === 0) {
          console.warn('Auth0 user has no organizations');
          setCurrentOrg(createEmptyOrg());
        } else {
          let targetOrg: Org | undefined;
          const currentOrgFromLocalStorage = localStorage.getItem('currentOrg');
          let storedOrg: Org | null = null;
          if (currentOrgFromLocalStorage) {
            try {
              storedOrg = JSON.parse(currentOrgFromLocalStorage) as Org;
            } catch {
              localStorage.removeItem('currentOrg');
            }
          }

          // Deep-link support: read ?org=<orgid> from URL. SSO JWT takes
          // priority, but flag a message when the link points elsewhere.
          const deepLinkOrgId = readOrgParam();

          // Prefer the org from the JWT claim — this is the org the user
          // authenticated against via SSO, so land them there.
          if (jwtOrgId) {
            targetOrg = orgs.find((org) => org.orgid === jwtOrgId);
            if (targetOrg && storedOrg?.orgid && storedOrg.orgid !== jwtOrgId) {
              const storedOrgInList = orgs.find(
                (org) => org.orgid === storedOrg?.orgid,
              );
              if (storedOrgInList) {
                setOrgSwitchBlockedMessage(
                  `You're signed in with SSO for ${targetOrg.name}. Your organization can't be changed here. To use a different organization, sign out and sign in with that organization or use email/OTP.`,
                );
              }
            }
            if (targetOrg && deepLinkOrgId && deepLinkOrgId !== jwtOrgId) {
              setOrgDeepLinkMessage({
                text: `You're signed in with SSO for ${targetOrg.name}. Can't open link in a different organization.`,
                severity: 'warning',
              });
            }
          }

          // Deep-link precedence when no JWT pin and user is a member
          if (!targetOrg && deepLinkOrgId) {
            const deepLinkOrg = orgs.find((org) => org.orgid === deepLinkOrgId);
            if (deepLinkOrg) {
              targetOrg = deepLinkOrg;
              if (!storedOrg?.orgid || storedOrg.orgid !== deepLinkOrgId) {
                setOrgDeepLinkMessage({
                  text: `Switched to ${deepLinkOrg.name} organization`,
                  severity: 'success',
                });
              }
            } else {
              console.warn(
                `Deep-link org '${deepLinkOrgId}' not accessible; falling back.`,
              );
              setOrgDeepLinkMessage({
                text: `You are not a member of the requested organization.`,
                severity: 'warning',
              });
            }
          }

          if (deepLinkOrgId) {
            stripOrgParamFromUrl();
          }

          // Fall back to localStorage, then first org
          if (!targetOrg && storedOrg?.orgid) {
            targetOrg = orgs.find((org) => org.orgid === storedOrg?.orgid);
          }

          if (!targetOrg) {
            targetOrg = orgs[0];
          }

          setCurrentOrg(targetOrg);
          localStorage.setItem('currentOrg', JSON.stringify(targetOrg));

          // Region enforcement is now handled server-side via
          // require_home_region() — non-home users are allowed through
          // with read-only access. No client-side redirect needed.

          // Update user with org-specific role
          userData.isOwner = targetOrg.user_role === 'owner';
          userData.is_infra_admin = targetOrg.is_infra_admin ?? false;
          setUser({ ...userData });

          analytics.identify(userData.id, {
            orgName: targetOrg.name,
            orgid: targetOrg.orgid,
            email: userData.email,
            name: `${userData.firstName} ${userData.lastName}`,
          });

          if (!isLocalhost) {
            Sentry.setUser({
              id: userData.id,
              email: userData.email,
              username: `${userData.firstName} ${userData.lastName}`,
            });
            Sentry.setContext('organization', {
              id: targetOrg.orgid,
              name: targetOrg.name,
              role: targetOrg.user_role,
            });
          }
        }
      } else {
        setCurrentOrg(createEmptyOrg());
      }

      setLoading(false);
      return true;
    } catch (error) {
      console.error('Auth0: handleAuth0Session failed', {
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : 'unknown',
        hasAuth0User: !!auth0User,
        timestamp: new Date().toISOString(),
      });

      Sentry.captureException(error, {
        extra: {
          context: 'auth0-session-initialization',
          hasAuth0User: !!auth0User,
          auth0Email: auth0User?.email,
        },
      });

      // Only clear Auth0 cache for stale-token errors (not transient/network).
      if (isStaleAuth0Error(error)) {
        clearAuth0Cache();
      }

      clearAuth0TokenProvider();
      setUser(null);
      setToken(null);
      setAuthType('otp');
      setLoading(false);
      return false;
    }
  }, [auth0User, getAccessTokenSilently, setToken, setAuthType, isLocalhost]);

  const getAuthData = useCallback(async () => {
    try {
      const [userResponse, tokenResponse] = await Promise.all([
        fetchUser(),
        getToken(),
      ]);

      if (userResponse?.detail) {
        setUser(null);
        setLoading(false);
        return { userResponse, tokenResponse };
      }

      if (tokenResponse?.detail) {
        setToken(null);
        setLoading(false);
        return { userResponse, tokenResponse };
      } else {
        setToken(tokenResponse.data?.token as string);
        const currentOrgFromLocalStorage = localStorage.getItem('currentOrg');

        const orgsResponse = await getAllOrgs();

        if (orgsResponse?.detail) {
          console.error('Failed to fetch orgs:', orgsResponse.detail);
          setOrgs([]);
        } else {
          setOrgs(orgsResponse.data as Org[]);
        }

        /**
         * Fallback mechanism for deleted organization members (see JS-1274):
         * 1. Check if user has no organizations - log them out.
         * 2. Validate if stored org still exists - fallback to first available org.
         * 3. If validation fails, clear invalid stored org and use first available.
         */
        if (!orgsResponse?.detail && orgsResponse?.data) {
          const orgs = orgsResponse.data as Org[];

          if (orgs.length === 0) {
            console.warn('User has no organizations, logging out');
            // Read before resetUser(), which sets it back to 'otp'.
            const { authType: currentAuthType } = useOrgStore.getState();

            try {
              await signOut();
            } catch (error) {
              Sentry.captureException(error, {
                extra: {
                  context: 'getAuthData: signing out user with no orgs',
                },
              });
            }

            // This is a logout, so it goes through the same teardown as any
            // other one. Clearing state by hand here skipped the Auth0 cache
            // wipe and left the refresh token live.
            broadcastLogout('user');
            resetUser();
            localStorage.removeItem('currentOrg');
            localStorage.removeItem(LAST_ACTIVITY_KEY);

            // resetUser() hands SSO users to Auth0's logout redirect; everyone
            // else still needs sending to the login page.
            if (currentAuthType !== 'auth0') {
              window.location.href = ROUTES.LOGIN;
            }
            return;
          }
        }

        // Validate if stored org still exists in user's organizations and use it directly
        let currentOrg: Org;
        if (!orgsResponse?.detail && orgsResponse?.data) {
          const orgs = orgsResponse.data as Org[];

          // Parse stored org once and reuse the parsed object
          let storedOrg: Org | null = null;
          let storedOrgId: string | null = null;
          if (currentOrgFromLocalStorage) {
            try {
              storedOrg = JSON.parse(currentOrgFromLocalStorage);
              storedOrgId = storedOrg?.orgid || null;
            } catch (error) {
              console.error(
                'Failed to parse stored org from localStorage:',
                error,
              );
              Sentry.captureException(error, {
                extra: {
                  storedValue: currentOrgFromLocalStorage,
                  context: 'auth-initialization',
                },
              });
              localStorage.removeItem('currentOrg');
            }
          }

          const isStoredOrgValid = storedOrgId
            ? orgs.some((org) => org.orgid === storedOrgId)
            : false;

          // Deep-link support: if URL carries ?org=<orgid> and the user is a
          // member, prefer it over the stored org. Strip the param after
          // consuming so refreshes don't re-trigger the switch.
          const deepLinkOrgId = readOrgParam();
          let targetOrg: Org | undefined;
          if (deepLinkOrgId) {
            const deepLinkOrg = orgs.find((org) => org.orgid === deepLinkOrgId);
            if (deepLinkOrg) {
              targetOrg = deepLinkOrg;
              if (!storedOrgId || storedOrgId !== deepLinkOrgId) {
                setOrgDeepLinkMessage({
                  text: `Switched to ${deepLinkOrg.name} organization`,
                  severity: 'success',
                });
              }
            } else {
              console.warn(
                `Deep-link org '${deepLinkOrgId}' not accessible; falling back.`,
              );
              setOrgDeepLinkMessage({
                text: `You are not a member of the requested organization.`,
                severity: 'warning',
              });
            }
            stripOrgParamFromUrl();
          }

          if (!targetOrg) {
            if (isStoredOrgValid && storedOrgId) {
              targetOrg = orgs.find((org) => org.orgid === storedOrgId);
            } else {
              // User was removed from stored org, use first available
              targetOrg = orgs[0];
              if (storedOrgId && !isStoredOrgValid) {
                console.warn(
                  'Stored organization no longer accessible, falling back to first available org',
                );
                localStorage.removeItem('currentOrg');
              }
            }
          }

          if (targetOrg) {
            currentOrg = targetOrg;

            // Update local storage if it differs from the target org
            if (
              !storedOrg ||
              JSON.stringify(storedOrg) !== JSON.stringify(currentOrg)
            ) {
              localStorage.setItem('currentOrg', JSON.stringify(currentOrg));
            }

            setCurrentOrg(currentOrg);

            analytics.identify(userResponse.data?.id, {
              orgName: currentOrg.name,
              orgid: currentOrg.orgid,
              email: userResponse.data?.email,
              name: `${userResponse.data?.firstName} ${userResponse.data?.lastName}`,
            });
          } else {
            console.error('No valid organization found');
            currentOrg = createEmptyOrg();
            setCurrentOrg(currentOrg);
          }
        } else {
          console.error('Failed to fetch orgs or no orgs data');
          currentOrg = createEmptyOrg();
          setCurrentOrg(currentOrg);
        }

        // is_infra_admin is a PER-ORG capability, so it comes from the selected
        // org on /organizations/me, which platform-api enriches from Postgres
        // (organization_x_members via clj-pg-wrapper).
        //
        // It deliberately does NOT come from the clj-pg-wrapper JWT claim.
        // That claim is derived from _get_primary_org(), which picks one
        // membership row arbitrarily (LIMIT 1, no ORDER BY) and reports its
        // flag as though it were global. For a user in several orgs it is
        // wrong whenever the arbitrary row isn't the org being viewed — and
        // since it was passed as an override and `??` does not fall through on
        // `false`, it masked the correct per-org value and hid the
        // infra-admin settings pages entirely.

        // Always update user with org-specific role after org info is resolved
        // This ensures isOwner is set correctly even if org fetch fails
        if (userResponse.data) {
          updateUser(userResponse.data as UserResponse, currentOrg);

          // Set Sentry user context
          if (!isLocalhost) {
            Sentry.setUser({
              id: userResponse.data.id,
              email: userResponse.data.email,
              username: `${userResponse.data.firstName} ${userResponse.data.lastName}`,
            });

            Sentry.setContext('organization', {
              id: currentOrg.orgid,
              name: currentOrg.name,
              role: currentOrg.user_role,
            });
          }
        }
      }

      setLoading(false);
      return { userResponse, tokenResponse };
    } catch (error) {
      console.error('Auth data fetch error:', error);
      setUser(null);
      setToken(null);
      setLoading(false);
      return {
        userResponse: { detail: 'Failed to fetch auth data' },
        tokenResponse: { detail: 'Failed to fetch token' },
      };
    }
    // TODO: fix the issue with setCurrentOrg and setOrgs causing infinite loops
    // intentionally avoid adding setCurrentOrg and setOrgs to prevent infinite loops
  }, [updateUser, setToken, isLocalhost]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleValidateInvite = useCallback(async () => {
    try {
      setIsValidating(true);

      if (!email || !code) {
        const error = new Error('Missing invite parameters');
        console.error('Invite validation failed: missing parameters', {
          hasEmail: !!email,
          hasCode: !!code,
          url: window.location.href,
        });

        if (!isLocalhost) {
          Sentry.captureException(error, {
            extra: {
              url: window.location.href,
              hasEmail: !!email,
              hasCode: !!code,
              email: email || 'missing',
              context: 'invite-validation',
            },
          });
        }

        return;
      }

      const response = await validateInvite({ email, code });

      if (response?.detail) {
        console.error('Validate invite error:', response.detail);
        return;
      }

      await getAuthData();
    } catch (error) {
      console.error('Validate invite error:', error);
      if (!isLocalhost) {
        Sentry.captureException(error, {
          extra: {
            context: 'invite-validation',
            email,
            hasCode: !!code,
          },
        });
      }
    } finally {
      setIsValidating(false);
    }
  }, [email, code, getAuthData, isLocalhost]);

  const resetUser = () => {
    const { authType } = useOrgStore.getState();

    // Revoke the Auth0 refresh-token grant before clearAuth0Cache() wipes it
    // from localStorage — otherwise the grant stays valid server-side.
    if (authType === 'auth0') {
      revokeAuth0RefreshTokens();
    }

    clearAuth0TokenProvider();
    setUser(null);
    setToken(null);
    setAuthType('otp');

    // Clear Auth0 cached state to prevent stale sessions on next page load.
    clearAuth0Cache();

    // Clear Sentry context
    if (!isLocalhost) {
      Sentry.setUser(null);
      Sentry.setContext('organization', null);
    }

    // If Auth0 session, log out from Auth0 as well (redirect to origin; we skip getAuthData once via flag)
    if (authType === 'auth0') {
      try {
        markLoggingOut();
        sessionStorage.setItem('auth_skip_next_check', '1');
        auth0Logout({ logoutParams: { returnTo: window.location.origin } });
      } catch (error) {
        clearLoggingOut();
        Sentry.captureException(error, {
          extra: { context: 'resetUser: auth0Logout' },
        });
      }
    }
  };

  useEffect(() => {
    // Wait for Auth0 SDK to finish initializing before deciding auth path
    if (isAuth0Loading) return;

    const currentPath = window.location.pathname;

    const handleAuth = async () => {
      // Ensure only one auth path runs at a time (avoid race between Auth0 and OTP/legacy)
      if (authInProgressRef.current) {
        console.log('Auth: handleAuth skipped — already in progress', {
          timestamp: new Date().toISOString(),
        });
        return;
      }

      authInProgressRef.current = true;
      console.log('Auth: handleAuth started', {
        isAuth0Authenticated,
        hasAuth0User: !!auth0User,
        currentPath,
        hasOrgToken: !!useOrgStore.getState().token,
        timestamp: new Date().toISOString(),
      });

      try {
        // Auth0 SSO path: user authenticated via Auth0
        if (isAuth0Authenticated && auth0User) {
          console.log('Auth: Taking Auth0 SSO path');
          const auth0Success = await handleAuth0Session();
          if (auth0Success) return;
          // Auth0 had stale cached state (e.g. missing refresh token).
          // Fall through to OTP/cookie path as a fallback.
          console.log('Auth: Auth0 failed, falling back to OTP/cookie path');
        }

        // OTP path: existing cookie-based auth
        if (currentPath.includes('/org/invite')) {
          await handleValidateInvite();
        } else if (currentPath.includes('/') && code && email) {
          try {
            setIsValidating(true);
            const result = await validateOtp({ email, code });

            if (result?.detail) {
              console.error('Validate OTP error:', result.detail);
              return;
            }

            await getAuthData();
          } catch (error) {
            console.error('Validate OTP error:', error);
          } finally {
            setIsValidating(false);
          }
        } else {
          // skip getAuthData if when first visiting the login page, before logging in
          if (currentPath === ROUTES.LOGIN) {
            setLoading(false);
            return;
          }
          // skip getAuthData if we're just logging out
          if (sessionStorage.getItem('auth_skip_next_check') === '1') {
            sessionStorage.removeItem('auth_skip_next_check');
            setLoading(false);
            return;
          }
          try {
            await getAuthData();
          } catch (error) {
            console.error('Get user error:', error);
          }
        }
      } finally {
        authInProgressRef.current = false;
      }
    };

    handleAuth();
    // TODO: fix the issue with currentPath causing infinite loops
    // Intentionally avoid adding `window.location.pathname` as a dependency to prevent infinite loops
  }, [
    isAuth0Loading,
    isAuth0Authenticated,
    auth0User,
    handleAuth0Session,
    code,
    email,
    getAuthData,
    handleValidateInvite,
  ]);

  useEffect(() => {
    if (cc) {
      analytics.setCookiePersistence(cc.acceptedCategory('analytics'));
    }
  }, [cc]);

  const clearOrgSwitchBlockedMessage = useCallback(() => {
    setOrgSwitchBlockedMessage(null);
  }, []);

  const clearOrgDeepLinkMessage = useCallback(() => {
    setOrgDeepLinkMessage(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isValidating,
        isAdmin,
        resetUser,
        updateUser,
        refetchAuth: getAuthData,
        orgSwitchBlockedMessage,
        clearOrgSwitchBlockedMessage,
        orgDeepLinkMessage,
        clearOrgDeepLinkMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
